<template>
  <div class="min-h-screen p-4 pt-8 md:pt-20">
    <div class="max-w-6xl mx-auto">
      <!-- Header Section -->
      <div class="text-center mb-8 md:mb-12">
        <div
          class="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl mb-4 md:mb-6"
        >
          <svg
            class="w-8 h-8 md:w-10 md:h-10 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
        </div>
        <h1
          class="text-[25px] md:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-3 md:mb-4"
        >
          Welcome to Educanium
        </h1>
        <p
          v-if="!allStepsCompleted"
          class="text-[16px] md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed"
        >
          Set up your school management system in <b>3</b> simple steps
        </p>
      </div>

      <!-- Progress Steps - Mobile Optimized -->
      <div class="mb-8 md:mb-12">
        <!-- Mobile: Vertical Layout -->
        <div
          class="block md:hidden space-y-4"
          v-if="!allStepsCompleted"
        >
          <div
            v-for="step in steps"
            :key="step.number"
            class="flex items-center p-4 rounded-2xl transition-all duration-300"
            :class="
              step.number === currentStepNumber
                ? 'bg-white shadow-lg border-2 border-indigo-200'
                : step.completed
                ? 'bg-green-50 border border-green-200'
                : 'bg-gray-50 border border-gray-200'
            "
          >
            <div
              class="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg transition-all duration-300 flex-shrink-0"
              :class="getStepClass(step.number)"
            >
              <span v-if="!step.completed">{{ step.number }}</span>
              <svg
                v-else
                class="w-6 h-6"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-4 flex-1">
              <p class="font-semibold text-gray-900 text-lg">
                {{ step.title }}
              </p>
              <p class="text-sm text-gray-600 mt-1">{{ step.description }}</p>
            </div>
          </div>
        </div>

        <!-- Desktop: Horizontal Layout -->
        <div class="hidden md:flex items-center justify-between">
          <div
            v-for="step in steps"
            :key="step.number"
            class="flex items-center"
            :class="{ 'flex-1': step.number < 4 }"
          >
            <div class="flex items-center">
              <div
                class="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-lg transition-all duration-300 shadow-lg"
                :class="getStepClass(step.number)"
              >
                <span v-if="!step.completed">{{ step.number }}</span>
                <svg
                  v-else
                  class="w-7 h-7"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <div class="ml-4 text-left">
                <p class="font-semibold text-gray-900 text-lg">
                  {{ step.title }}
                </p>
                <p class="text-sm text-gray-600">{{ step.description }}</p>
              </div>
            </div>
            <div
              v-if="step.number < 4"
              class="flex-1 h-2 mx-8 rounded-full transition-all duration-300"
              :class="
                step.completed
                  ? 'bg-gradient-to-r from-green-400 to-green-500'
                  : 'bg-gray-200'
              "
            ></div>
          </div>
        </div>
      </div>

      <!-- Main Content Card -->
      <div
        v-if="!allStepsCompleted"
        class="bg-white rounded-3xl shadow-2xl p-6 md:p-10 mb-8 border border-gray-100"
      >
        <div class="text-center">
          <h2 class="text-2xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
            {{ currentStep.title }}
          </h2>
          <p
            class="text-base md:text-lg text-gray-600 mb-8 md:mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            {{ currentStep.longDescription }}
          </p>

          <div
            class="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <button
              @click="navigateToStep"
              class="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-white bg-gradient-to-r from-purple-500 to-indigo-500 shadow-lg ring-1 ring-white/40 hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/60"
              :disabled="loading"
            >
              <span
                v-if="!loading"
                class="flex items-center justify-center"
              >
                {{ currentStep.buttonText }}
                <svg
                  class="w-5 h-5 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </span>
              <span
                v-else
                class="flex items-center justify-center"
              >
                <svg
                  class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Success Animation & Completion Card -->
      <div
        v-if="allStepsCompleted"
        class="relative overflow-hidden bg-white border border-slate-100 rounded-3xl p-8 sm:p-12 text-center shadow-xl shadow-slate-100/80 max-w-2xl mx-auto font-sans"
      >
        <!-- Subtle Ambient Glow -->
        <div
          class="absolute -top-24 left-1/2 -translate-x-1/2 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"
        ></div>

        <!-- Polished Success Icon -->
        <div class="relative mb-6">
          <div
            class="w-16 h-16 sm:w-20 sm:h-20 bg-emerald-50 text-emerald-600 rounded-3xl flex items-center justify-center mx-auto shadow-sm ring-8 ring-emerald-50/60"
          >
            <svg
              class="w-8 h-8 sm:w-10 sm:h-10"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4.5 12.75l6 6 9-13.5"
              />
            </svg>
          </div>
        </div>

        <!-- Text Content -->
        <div class="relative z-10">
          <span
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-semibold uppercase tracking-wider mb-3"
          >
            <span
              class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"
            ></span>
            Setup Completed
          </span>

          <h2
            class="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mb-2"
          >
            You're Ready to Go!
          </h2>

          <p
            class="text-sm sm:text-base text-slate-500 mb-8 max-w-md mx-auto leading-relaxed"
          >
            Your system configuration is complete. You can now start managing
            students, teachers, and smart terminals seamlessly.
          </p>

          <!-- Action Button -->
          <button
            @click="goToDashboard"
            class="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-slate-900 hover:bg-slate-800 active:scale-[0.98] text-white font-semibold text-sm rounded-2xl shadow-md hover:shadow-lg transition-all duration-200"
          >
            <span>Go to Dashboard</span>
            <svg
              class="w-4 h-4 text-slate-400 group-hover:text-white transition-colors"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapActions } from "vuex";

  export default {
    name: "Onboarding",
    data() {
      return {
        loading: false,
        steps: [
          {
            number: 1, // Signup
            title: "Signup",
            description: "Your account is ready",
            longDescription:
              "You have successfully signed up and can now set up your school.",
            buttonText: "Next",
            route: null, // no route needed
            completed: true, // always complete
          },
          {
            number: 2, // Create School
            title: "Create School",
            description: "Set up your school profile",
            longDescription:
              "Start by creating your school profile with basic information like name, address, and contact details. This will be the foundation of your education management system.",
            buttonText: "Create School",
            route: { name: "createSchool" },
            completed: false,
          },
          {
            number: 3, // Add Classes
            title: "Add Classes",
            description: "Create your first class",
            longDescription:
              "Set up classes for your school. You can organize students by grade, section, or any other criteria that works for your institution.",
            buttonText: "Create Classes",
            route: { name: "Class" },
            completed: false,
          },
        ],
        hasFetchedClasses: false,
      };
    },
    computed: {
      ...mapState(["user", "classes"]),

      validClasses() {
        return (this.classes || []).filter(
          (cls) => cls.name.toLowerCase() !== "passout",
        );
      },

      currentStepNumber() {
        // Step 1 is always complete (Signup)
        if (!this.user?.schoolId) return 2;
        if (this.validClasses.length === 0) return 3;
        return 4; // all steps completed
      },

      currentStep() {
        if (this.currentStepNumber <= 3) {
          return this.steps[this.currentStepNumber - 1]; // array index = stepNumber - 1
        }
        return this.steps[2]; // last step
      },

      allStepsCompleted() {
        return this.currentStepNumber > 3;
      },
    },
    watch: {
      "user.schoolId": {
        async handler(newVal) {
          if (newVal && !this.hasFetchedClasses) {
            await this.fetchAllClasses();
            this.hasFetchedClasses = true;
          }
        },
        immediate: true,
      },
    },
    async beforeMount() {
      await this.$store.dispatch("initializeApp");
      this.updateStepCompletion();
    },
    methods: {
      ...mapActions(["fetchAllClasses"]),

      updateStepCompletion() {
        this.steps[1].completed = !!this.user?.schoolId;
        this.steps[2].completed = this.validClasses.length > 0;
      },

      getStepClass(stepNumber) {
        const step = this.steps[stepNumber - 1]; // adjust for 1-based numbering
        if (step.completed) {
          return "bg-gradient-to-r from-green-500 to-emerald-500 shadow-lg";
        } else if (step.number === this.currentStepNumber) {
          return "bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg ring-4 ring-indigo-200";
        } else {
          return "bg-gray-300";
        }
      },

      navigateToStep() {
        const step = this.currentStep;
        if (!step.route) return; // Skip signup step
        this.loading = true;
        setTimeout(() => {
          this.$router.replace(step.route);
          this.loading = false;
        }, 500);
      },

      goToDashboard() {
        this.$store.commit("SET_ONBOARDING_COMPLETED", true);
        this.$router.replace("/");
      },
    },
  };
</script>

<style scoped>
  /* Additional custom styles for animations */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in-up {
    animation: fade-in-up 0.8s ease-out;
  }

  @keyframes bounce-slow {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }

  .animate-bounce-slow {
    animation: bounce-slow 2s infinite;
  }
</style>
